import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Eye, EyeOff, Save, X, GripVertical } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API } from "@/apiConfig";
import ContentPublicPreview, { announceContentSaved } from "@/components/ContentPublicPreview";
const empty = { question: "", answer: "", order: 0, visible: true };

export default function GestionFAQ() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [initialForm, setInitialForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const load = () => axios.get(`${API}/admin/faq`).then(r => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => toast.error("Erreur"));

  useEffect(() => { load(); }, []);

  const openNew = () => { const next={...empty,order:items.length+1};setForm(next);setInitialForm(next);setEditing("new"); };
  const openEdit = f => { const next={ question: f.question, answer: f.answer, order: f.order, visible: f.visible };setForm(next);setInitialForm(next);setEditing(f.id); };
  const close = () => { setEditing(null);setForm({...empty});setInitialForm({...empty}); };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing === "new") await axios.post(`${API}/admin/faq`, form);
      else await axios.put(`${API}/admin/faq/${editing}`, form);
      announceContentSaved();
      toast.success("FAQ mise à jour !");
      close(); load();
    } catch { toast.error("Erreur"); } finally { setLoading(false); }
  };

  const toggleVisible = async item => {
    await axios.put(`${API}/admin/faq/${item.id}`, { ...item, visible: !item.visible });
    load();
  };

  const handleDelete = async id => {
    if (!window.confirm("Supprimer ?")) return;
    await axios.delete(`${API}/admin/faq/${id}`);
    toast.success("Supprimé"); load();
  };

  if (editing !== null) {
    return (
      <ContentPublicPreview url="/#faq" focusHash="#faq" title="Section FAQ" status={editing!=="new"&&form.visible?"published":"draft"} dirty={JSON.stringify(form)!==JSON.stringify(initialForm)}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="font-playfair text-3xl text-charcoal">{editing === "new" ? "Nouvelle question" : "Modifier"}</h1>
          <button onClick={close} className="p-2 text-charcoal-muted hover:text-charcoal"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 border border-sage/10 space-y-5">
          <div className="grid grid-cols-4 gap-5">
            <div className="col-span-3">
              <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Question *</label>
              <input value={form.question} onChange={e => setForm(f => ({ ...f, question: e.target.value }))} required placeholder="Votre question ici..."
                className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none" />
            </div>
            <div>
              <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Ordre</label>
              <input type="number" value={form.order} onChange={e => setForm(f => ({ ...f, order: parseInt(e.target.value) || 0 }))} min={0}
                className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none" />
            </div>
          </div>
          <div>
            <label className="font-manrope text-xs text-charcoal-muted mb-1.5 block">Réponse *</label>
            <textarea value={form.answer} onChange={e => setForm(f => ({ ...f, answer: e.target.value }))} rows={5} required placeholder="La réponse détaillée..."
              className="w-full border border-sage/20 focus:border-sage rounded-xl px-4 py-2.5 font-manrope text-sm outline-none resize-none" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.visible} onChange={e => setForm(f => ({ ...f, visible: e.target.checked }))} className="accent-sage" />
            <span className="font-manrope text-sm text-charcoal">Visible sur le site</span>
          </label>
          <div className="flex gap-3">
            <button type="submit" disabled={loading} data-testid="faq-save-btn" className="btn-primary flex items-center gap-2 disabled:opacity-60">
              {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Save size={15} /> Enregistrer</>}
            </button>
            <button type="button" onClick={close} className="btn-secondary">Annuler</button>
          </div>
        </form>
      </div>
      </ContentPublicPreview>
    );
  }

  return (
    <ContentPublicPreview url="/#faq" focusHash="#faq" title="Section FAQ">
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-playfair text-3xl text-charcoal">FAQ</h1>
          <p className="font-manrope text-sm text-charcoal-muted">{items.length} question{items.length > 1 ? "s" : ""}</p>
        </div>
        <button onClick={openNew} data-testid="new-faq-btn" className="btn-primary flex items-center gap-2"><Plus size={16} /> Ajouter</button>
      </div>

      <div className="bg-white rounded-2xl border border-sage/10 overflow-hidden">
        {items.length === 0 ? (
          <div className="py-16 text-center font-manrope text-charcoal-muted">Aucune question.</div>
        ) : items.map((item, i) => (
          <div key={item.id || i} data-testid={`faq-item-${i}`} className={`flex items-start gap-4 px-5 py-4 border-b border-sage/5 last:border-0 hover:bg-bone/20 transition-colors ${!item.visible ? "opacity-50" : ""}`}>
            <GripVertical size={16} className="text-charcoal-soft mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-manrope font-medium text-sm text-charcoal">{item.question}</p>
              <p className="font-manrope text-xs text-charcoal-muted mt-1 line-clamp-2">{item.answer}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <span className="font-manrope text-xs text-charcoal-soft mr-2">#{item.order}</span>
              <button onClick={() => openEdit(item)} className="p-1.5 text-charcoal-muted hover:text-sage transition-colors"><Edit size={14} /></button>
              <button onClick={() => toggleVisible(item)} className="p-1.5 text-charcoal-muted hover:text-sage transition-colors">
                {item.visible ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
              <button onClick={() => handleDelete(item.id)} className="p-1.5 text-charcoal-muted hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
    </ContentPublicPreview>
  );
}
